/**
 * LeadsIndexPage
 *
 * Portfolio-wide leads index with filters and navigation to Lead detail.
 * Route: /leasing/leads
 * US-8 — See every Lead across all Listings
 */

import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { VariantProps } from 'class-variance-authority';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import type { badgeVariants } from '../../components/ui/badge-variants';
import { Skeleton } from '../../components/ui/skeleton';
import { AddLeadModal } from './modals/AddLeadModal.js';
import { useUnseenCounts } from '../../hooks/useAwareness';
import { MultiSelectFilter, type MultiSelectFilterOption } from '../../components/ui/multi-select-filter';
import {
  fetchLeads,
  fetchListings,
  fetchUnits,
  type Lead,
  type LeadStage,
} from '../../lib/leasing.api';
import { formatUnitAddress } from '@resida/shared';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

const STAGE_LABELS: Record<LeadStage, string> = {
  inquiring: 'Inquiring',
  showing: 'Showing',
  applying: 'Applying',
  verifying: 'Verifying',
  deciding: 'Deciding',
  closed: 'Closed',
};

const STAGE_VARIANTS: Record<LeadStage, BadgeVariant> = {
  inquiring: 'info',
  showing: 'primary',
  applying: 'warning',
  verifying: 'warning',
  deciding: 'success',
  closed: 'secondary',
};

const ALL_STAGES: LeadStage[] = ['inquiring', 'showing', 'applying', 'verifying', 'deciding', 'closed'];

const STAGE_OPTIONS_MULTI: MultiSelectFilterOption<LeadStage>[] = ALL_STAGES.map((s) => ({
  value: s,
  label: STAGE_LABELS[s],
}));

function formatAge(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

export function LeadsIndexPage() {
  const navigate = useNavigate();

  // Add Lead modal state
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);

  // Listing combobox state
  const [listingSearch, setListingSearch] = useState('');
  const [listingFilterId, setListingFilterId] = useState('');
  const [listingDropdownOpen, setListingDropdownOpen] = useState(false);
  const listingComboboxRef = useRef<HTMLDivElement>(null);

  // AC-7: every stage except closed is pre-selected by default.
  const [stageFilter, setStageFilter] = useState<LeadStage[]>(['inquiring', 'showing', 'applying', 'verifying', 'deciding']);
  const [unrespondedOnly, setUnrespondedOnly] = useState(false);
  const [prospectSearch, setProspectSearch] = useState('');
  const [sortBy, setSortBy] = useState<'recent' | 'stage'>('recent');
  const [page, setPage] = useState(1);

  // Fetch listings and units for the combobox
  const { data: listingsData } = useQuery({
    queryKey: ['listings-for-filter'],
    queryFn: () => fetchListings({ pageSize: 100 }),
    staleTime: 300_000,
  });

  const { data: unitsData } = useQuery({
    queryKey: ['units-for-filter'],
    queryFn: () => fetchUnits(),
    staleTime: 300_000,
  });

  // Build listing options from listings + units join
  const listingOptions = useMemo(() => {
    const listings = listingsData?.items ?? [];
    const units = unitsData?.units ?? [];
    const unitMap = new Map(units.map((u) => [u.id, u]));
    return listings.map((l) => {
      const unit = unitMap.get(l.unitId);
      const label = unit
        ? formatUnitAddress(unit.address1, unit.unitNumber, ' · ')
        : l.id;
      return { id: l.id, label };
    });
  }, [listingsData, unitsData]);

  const filteredListingOptions = listingSearch
    ? listingOptions.filter((o) => o.label.toLowerCase().includes(listingSearch.toLowerCase()))
    : listingOptions;

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (listingComboboxRef.current && !listingComboboxRef.current.contains(e.target as Node)) {
        setListingDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['leads', { listingId: listingFilterId, stage: stageFilter, unrespondedOnly, sortBy, page }],
    queryFn: () =>
      fetchLeads({
        listingId: listingFilterId || undefined,
        stage: stageFilter,
        unrespondedOnly: unrespondedOnly || undefined,
        sortBy,
        page,
        pageSize: 20,
      }),
  });

  const handleStageChange = useCallback((next: LeadStage[]) => {
    setStageFilter(next);
    setPage(1);
  }, []);

  const handleUnrespondedToggle = useCallback(() => {
    setUnrespondedOnly((prev) => !prev);
    setPage(1);
  }, []);

  // DC-4/BR-16 (docs/features/change-awareness/specification.md) — per-lead ambient-tier
  // unseen marks, "on the thing itself" (a lead row here) rather than as a portfolio-wide
  // aggregate with no entity context. Covers every leasing_pipeline class whose
  // referenceType is 'lead' (showing_response_received, showing_scheduled, applicant_
  // submitted, lead_message_received, etc) — distinct from `lead.hasUnread` below, which is
  // driven only by unread ChannelMessages and predates this awareness system.
  const { data: unseenCountsData } = useUnseenCounts();
  const unseenByLeadId = useMemo(() => new Map(
    (unseenCountsData?.byInstance ?? [])
      .filter((entry) => entry.referenceType === 'lead')
      .map((entry) => [entry.referenceId, entry.count])
  ), [unseenCountsData]);

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.ceil(totalCount / 20);

  // Filter by prospect name client-side (search is not in query params for this endpoint)
  const filteredItems = prospectSearch
    ? items.filter((lead) =>
        (lead.prospectName ?? lead.prospectEmail)
          .toLowerCase()
          .includes(prospectSearch.toLowerCase())
      )
    : items;

  return (
    <div data-testid="leads-index-page" className="w-full max-w-[1600px] mx-auto pb-6 px-[var(--page-padding-x)] pt-6">
      {/* Page header — LeasingLayout already renders the page-level <h1>Leads</h1>; this
          row only needs the action button, right-aligned. */}
      <div className="mb-4 flex items-center justify-end gap-3">
        <Button
          type="button"
          size="sm"
          data-testid="add-lead-button"
          onClick={() => setShowAddLeadModal(true)}
          aria-label="Add a lead manually"
        >
          + Add Lead
        </Button>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {/* Listing combobox */}
        <div ref={listingComboboxRef} className="relative flex items-center gap-2" data-testid="leads-listing-filter">
          <label htmlFor="leads-listing-search" className="text-sm text-muted-foreground shrink-0">
            Listing:
          </label>
          <div className="relative">
            <input
              id="leads-listing-search"
              data-testid="leads-listing-search"
              type="text"
              value={listingSearch}
              onFocus={() => setListingDropdownOpen(true)}
              onChange={(e) => {
                setListingSearch(e.target.value);
                setListingFilterId('');
                setListingDropdownOpen(true);
                setPage(1);
              }}
              placeholder="Filter by listing…"
              autoComplete="off"
              className="border border-input rounded-md px-3 py-1.5 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring min-w-[200px] pr-7"
              aria-label="Filter by listing name"
              aria-expanded={listingDropdownOpen}
              aria-haspopup="listbox"
            />
            {listingSearch && (
              <button
                type="button"
                aria-label="Clear listing filter"
                onClick={() => {
                  setListingSearch('');
                  setListingFilterId('');
                  setListingDropdownOpen(false);
                  setPage(1);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            )}
            {listingDropdownOpen && filteredListingOptions.length > 0 && (
              <ul
                role="listbox"
                className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-popover shadow-md text-sm"
              >
                {filteredListingOptions.map((opt) => (
                  <li
                    key={opt.id}
                    role="option"
                    aria-selected={opt.id === listingFilterId}
                    data-testid={`listing-option-${opt.id}`}
                    className="cursor-pointer px-3 py-2 hover:bg-accent hover:text-accent-foreground"
                    onMouseDown={(e) => {
                      e.preventDefault(); // prevent blur before click
                      setListingSearch(opt.label);
                      setListingFilterId(opt.id);
                      setListingDropdownOpen(false);
                      setPage(1);
                    }}
                  >
                    {opt.label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Stage filter */}
        <div className="flex items-center gap-2">
          <MultiSelectFilter
            testIdPrefix="leads-stage"
            label="Stage"
            options={STAGE_OPTIONS_MULTI}
            selected={stageFilter}
            onChange={handleStageChange}
          />
        </div>

        {/* Prospect search */}
        <div className="flex items-center gap-2">
          <label htmlFor="leads-prospect-search" className="text-sm text-muted-foreground shrink-0">
            Prospect:
          </label>
          <input
            id="leads-prospect-search"
            data-testid="leads-prospect-search"
            type="text"
            value={prospectSearch}
            onChange={(e) => setProspectSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="border border-input rounded-md px-3 py-1.5 text-sm bg-background text-foreground outline-none focus:ring-2 focus:ring-ring min-w-[200px]"
            aria-label="Search by prospect name or email"
          />
        </div>

        {/* Unresponded toggle */}
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            data-testid="leads-unresponded-toggle"
            checked={unrespondedOnly}
            onChange={handleUnrespondedToggle}
            className="rounded border-input"
            aria-label="Show only leads needing reply"
          />
          Needs reply only
        </label>

        {/* Sort toggle */}
        <div className="flex items-center gap-1" data-testid="leads-sort-toggle">
          <button
            type="button"
            data-testid="leads-sort-recent"
            onClick={() => { setSortBy('recent'); setPage(1); }}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
              sortBy === 'recent'
                ? 'bg-primary text-primary-foreground'
                : 'border border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            }`}
            aria-pressed={sortBy === 'recent'}
          >
            Recent
          </button>
          <button
            type="button"
            data-testid="leads-sort-stage"
            onClick={() => { setSortBy('stage'); setPage(1); }}
            className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
              sortBy === 'stage'
                ? 'bg-primary text-primary-foreground'
                : 'border border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground'
            }`}
            aria-pressed={sortBy === 'stage'}
          >
            By Stage
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          data-testid="leads-error"
          className="p-3 mb-4 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm flex items-center justify-between gap-3"
          role="alert"
        >
          <span>{error instanceof Error ? error.message : 'Failed to load leads'}</span>
          <Button variant="outline" size="sm" onClick={() => void refetch()} data-testid="leads-retry">
            Retry
          </Button>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div data-testid="leads-loading" className="space-y-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <>
          {filteredItems.length === 0 ? (
            <div data-testid="leads-empty" className="py-16 text-center text-muted-foreground">
              <p className="text-base font-medium mb-1">No leads found</p>
              <p className="text-sm" data-testid="leads-empty-message">
                {/* AC-8: an explicit empty-selection message when every stage is
                    deselected, distinct from the generic "no results" copy. */}
                {stageFilter.length === 0
                  ? 'Select at least one stage to see leads.'
                  : unrespondedOnly
                    ? 'No leads are awaiting a reply.'
                    : 'Leads will appear here when prospects contact your listings.'}
              </p>
            </div>
          ) : (
            <div data-testid="leads-index-table" className="relative">
              {/* AC-L2 mobile fallback: same overflow-x-auto + edge-fade pattern as
                  ListingsIndexPage's table — see AC-L1 there. */}
              <div
                aria-hidden="true"
                className="md:hidden pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent z-10"
              />
              <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm" aria-label="Leads">
                <thead>
                  <tr className="border-b border-border">
                    <th scope="col" className="text-left py-3 px-3 font-medium text-muted-foreground">Prospect</th>
                    <th scope="col" className="text-left py-3 px-3 font-medium text-muted-foreground">Listing</th>
                    <th scope="col" className="text-left py-3 px-3 font-medium text-muted-foreground">Stage</th>
                    <th scope="col" className="text-left py-3 px-3 font-medium text-muted-foreground">Last Activity</th>
                    <th scope="col" className="py-3 px-3"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((lead) => (
                    <LeadRow
                      key={lead.id}
                      lead={lead}
                      onOpen={() => navigate(`/leasing/leads/${lead.id}`)}
                      unseenCount={unseenByLeadId.get(lead.id) ?? 0}
                    />
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {totalPages > 1 && (
            <div data-testid="leads-pagination" className="mt-6 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages} ({totalCount} total)
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  data-testid="leads-prev-page"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="leads-next-page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {showAddLeadModal && (
        <AddLeadModal
          listings={listingOptions.map((opt) => ({ id: opt.id, name: opt.label }))}
          onClose={() => setShowAddLeadModal(false)}
          onSuccess={() => void refetch()}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lead Row Component
// ---------------------------------------------------------------------------

interface LeadRowProps {
  lead: Lead;
  onOpen: () => void;
  /** DC-4/BR-16 — un-cleared awareness item count for THIS lead instance (0 = no mark). */
  unseenCount: number;
}

function LeadRow({ lead, onOpen, unseenCount }: LeadRowProps) {
  const stageVariant = STAGE_VARIANTS[lead.stage] ?? 'secondary';
  const locationLabel = lead.propertyAddress
    ? formatUnitAddress(lead.propertyAddress, lead.unitLabel, ' · ')
    : 'Unknown property';

  return (
    <tr
      data-testid={`lead-row-${lead.id}`}
      className="border-b border-border hover:bg-accent/50 cursor-pointer transition-colors"
      onClick={onOpen}
    >
      <td className="py-3 px-3">
        <div className="flex items-center gap-2">
          <div className="font-medium text-foreground" data-testid={`lead-prospect-name-${lead.id}`}>
            {lead.prospectName ?? lead.prospectEmail}
          </div>
          {lead.hasUnread && (
            <span
              data-testid={`lead-unread-dot-${lead.id}`}
              aria-label="Unread message"
              className="inline-block h-2 w-2 rounded-full bg-info shrink-0"
            />
          )}
          {unseenCount > 0 && (
            <Badge
              variant="primary"
              data-testid={`lead-unseen-${lead.id}`}
              aria-label={`${unseenCount} unseen update${unseenCount === 1 ? '' : 's'}`}
              className="gap-1"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-primary" aria-hidden="true" />
              {unseenCount > 1 ? `${unseenCount > 99 ? '99+' : unseenCount} new` : 'New'}
            </Badge>
          )}
        </div>
        {lead.prospectName && (
          <div className="text-xs text-muted-foreground mt-0.5">{lead.prospectEmail}</div>
        )}
      </td>
      <td className="py-3 px-3 text-muted-foreground text-sm" data-testid={`lead-listing-label-${lead.id}`}>
        {locationLabel}
      </td>
      <td className="py-3 px-3">
        <Badge data-testid={`lead-stage-badge-${lead.id}`} variant={stageVariant}>
          {STAGE_LABELS[lead.stage]}
        </Badge>
      </td>
      <td className="py-3 px-3 text-sm text-muted-foreground">
        {formatAge(lead.lastActivityAt)}
      </td>
      <td className="py-3 px-3 text-right">
        <Button
          variant="outline"
          size="sm"
          data-testid={`lead-open-${lead.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
        >
          Open
        </Button>
      </td>
    </tr>
  );
}
